/**
 * PaymentCard — the booking detail view's money panel (BON-145), plus the
 * settled-refund rows (BON-335).
 *
 * Split out of the former `BookingDetailCards.tsx` (BON-335 round 4). See
 * CustomerCard.tsx for why. `payRow` is a plain lowercase function rather
 * than a component: it is used only from this file, which is the convention
 * the refund drawer documents for exactly this case.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import type {
  BookingDetail,
  BookingGiftCardRedemption,
  BookingIssuedGiftPass,
  BookingRefund,
  GiftPassStatus,
} from '../../api/bookingsV2';

import { cardCls } from './bookingCardShared';
import { formatCents, wasServiceFeeCharged } from './bookingDetailModel';
import { refundMethodLabel } from './refundMoneyRows';

function truncateId(id: string): string {
  return id.length > 16 ? `${id.slice(0, 10)}…${id.slice(-3)}` : id;
}

function payRow({
  label,
  value,
  sub,
  strong,
}: {
  label: string;
  value: string;
  sub?: ReactNode;
  strong?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between py-2">
      <div>
        <span
          className={
            strong
              ? 'text-sm font-semibold text-bark-900 dark:text-stone-100'
              : 'text-sm text-stone-600 dark:text-stone-300'
          }
        >
          {label}
        </span>
        {sub && <span className="ml-2 text-xs text-stone-400">{sub}</span>}
      </div>
      <span
        className={
          strong
            ? 'text-sm font-bold text-bark-900 dark:text-stone-100'
            : 'text-sm text-bark-900 dark:text-stone-100'
        }
      >
        {value}
      </span>
    </div>
  );
}

/** Deliberately NOT branching on REFUNDED. It used to, which relabelled this
 *  row "Refunded" while it still rendered `totalCents` — so a booking whose
 *  receipt and net cash differ reported a refund amount that never moved
 *  ($94.53 shown against a $44.53 refund, found in manual testing). The
 *  receipt is what was invoiced and does not change when money goes back; the
 *  refund is its own row, below, sourced from the refund record. */
function paymentTotalLabel(isComp: boolean, status: string): string {
  if (isComp) return 'No charge (comp)';
  if (status === 'PENDING_PAYMENT') return 'Amount due';
  return 'Total charged';
}

/** The settled refund, when there is one. Renders the amount from the refund
 *  record — never the receipt total — and links the minted card where one
 *  exists, following the same never-a-dead-link rule as the audit entry:
 *  plain text when the id is absent, rather than a link that 404s. */
function refundedRows(refund: BookingRefund) {
  const destination = refundMethodLabel(refund.method);
  return (
    <>
      {payRow({ label: 'Refunded', value: formatCents(refund.amountCents), strong: true })}
      <div className="flex items-start justify-between gap-3 py-2 text-xs text-stone-500 dark:text-stone-400">
        <span>
          Returned to{' '}
          {refund.giftCardId ? (
            <Link
              to={`/admin/gift-cards/${refund.giftCardId}`}
              className="font-semibold text-moss-700 underline dark:text-moss-300"
            >
              {destination}
            </Link>
          ) : (
            destination
          )}
        </span>
        {refund.externalReference && (
          <span className="flex-shrink-0 font-mono" title={refund.externalReference}>
            {refund.externalReference}
          </span>
        )}
      </div>
    </>
  );
}

const PASS_STATUS_LABEL: Record<GiftPassStatus, string> = {
  ISSUED: 'Not used yet',
  SENT: 'Sent to someone',
  REDEEMED: 'Used',
  VOIDED: 'Voided',
  EXPIRED: 'Expired',
};

/** The passes a refund issued from this booking, so staff can trace from the
 *  booking to each one. A pass is a seat, so the refund row counts passes;
 *  the cash the booking held when it was refunded sits beside it. */
function issuedPassRows(passes: BookingIssuedGiftPass[], refund: BookingRefund | null) {
  const noun = passes.length === 1 ? 'gift pass' : 'gift passes';
  return (
    <>
      {payRow({
        label: 'Refunded',
        sub: refund ? `${formatCents(refund.amountCents)} paid` : undefined,
        value: `${passes.length} ${noun}`,
        strong: true,
      })}
      <ul aria-label="Gift passes issued from this booking" className="py-2">
        {passes.map((pass) => (
          <li
            key={pass.id}
            className="flex items-center justify-between py-0.5 text-xs text-stone-500 dark:text-stone-400"
          >
            <span className="font-mono text-bark-900 dark:text-stone-100">{pass.codeMasked}</span>
            <span>{PASS_STATUS_LABEL[pass.status] ?? pass.status}</span>
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * The ticket rows of a booking some seats of which gift passes paid for: every
 * ticket at its list price, a row per pass taking one ticket off, then any
 * campaign discount on the rest, so the rows add up to the total.
 */
function passPaidTicketRows(booking: BookingDetail) {
  const { money, giftPassesRedeemed: giftPasses } = booking;
  const listCents = money.subtotalCents + money.passCoveredCents + money.campaignDiscountCents;
  const listUnitCents = money.ticketsCount > 0 ? listCents / money.ticketsCount : 0;
  const perPassCents = giftPasses.length > 0 ? money.passCoveredCents / giftPasses.length : 0;
  const passRows =
    perPassCents === listUnitCents
      ? giftPasses.map((pass, index) => (
          <div key={`${pass.codeMasked}-${index}`}>
            {payRow({
              label: 'Gift pass',
              sub: pass.codeMasked,
              value: `−${formatCents(perPassCents)}`,
            })}
          </div>
        ))
      : payRow({ label: 'Gift passes', value: `−${formatCents(money.passCoveredCents)}` });
  return (
    <>
      {payRow({
        label: 'Tickets',
        sub: `${formatCents(listUnitCents)} × ${money.ticketsCount}`,
        value: formatCents(listCents),
      })}
      {passRows}
      {money.campaignDiscountCents > 0 &&
        payRow({ label: 'Sale discount', value: `−${formatCents(money.campaignDiscountCents)}` })}
    </>
  );
}

function giftCardRedemptionRows(redemptions: BookingGiftCardRedemption[]) {
  return redemptions.map((redemption) => (
    <div key={redemption.giftCardId}>
      {payRow({
        label: 'Paid with gift card',
        sub: (
          <Link
            to={`/admin/gift-cards/${redemption.giftCardId}`}
            className="font-mono text-moss-700 underline dark:text-moss-300"
          >
            {redemption.codeMasked}
          </Link>
        ),
        value: formatCents(redemption.amountCents),
      })}
    </div>
  ));
}

function paymentHeaderRef(booking: BookingDetail, redemptions: BookingGiftCardRedemption[]) {
  if (booking.isComp) {
    return (
      <span className="text-[11px] px-2 py-0.5 rounded-md bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 font-medium">
        Comped
      </span>
    );
  }
  const paymentIntentId = booking.stripe?.paymentIntentId;
  if (paymentIntentId) {
    return (
      <span className="text-xs font-mono text-stone-400" title={paymentIntentId}>
        {truncateId(paymentIntentId)}
      </span>
    );
  }
  return (
    <span className="text-xs text-stone-400">{redemptions.length > 0 ? 'Gift card' : '—'}</span>
  );
}

function refundSection(booking: BookingDetail) {
  // `?? []`: an older API without the field must not white-screen the page.
  const passes = booking.giftPassesIssued ?? [];
  if (passes.length > 0) return issuedPassRows(passes, booking.refund);
  return booking.refund ? refundedRows(booking.refund) : null;
}

export function PaymentCard({ booking }: { booking: BookingDetail }) {
  const { money, isComp, status } = booking;
  const unit =
    money.ticketsCount > 0 ? formatCents(money.subtotalCents / money.ticketsCount) : null;
  const totalLabel = paymentTotalLabel(isComp, status);
  // `?? []`: an older API without the field must not white-screen the page.
  const giftCardRedemptions = booking.giftCardsRedeemed ?? [];

  return (
    <div className={cardCls}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200 dark:border-stone-700">
        <h2 className="text-sm font-semibold text-bark-900 dark:text-stone-100">Payment</h2>
        {paymentHeaderRef(booking, giftCardRedemptions)}
      </div>
      <div className="px-5 py-3 divide-y divide-stone-100 dark:divide-stone-700/50">
        {money.passCoveredCents > 0
          ? passPaidTicketRows(booking)
          : payRow({
              label: 'Tickets',
              sub: unit ? `${unit} × ${money.ticketsCount}` : undefined,
              value: formatCents(money.subtotalCents),
            })}
        {wasServiceFeeCharged(money.feesCents) &&
          payRow({ label: 'Service fee', value: formatCents(money.feesCents) })}
        {payRow({ label: 'Tax', value: formatCents(money.taxesCents) })}
        {payRow({ label: totalLabel, value: formatCents(money.totalCents), strong: true })}
        {giftCardRedemptionRows(giftCardRedemptions)}
        {refundSection(booking)}
      </div>
    </div>
  );
}
